import type { ReactElement } from 'react';
import type { AudienceSegment, DocumentSectionContent, Proposal, TacticUsageItem } from '@shared/types';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableBullets, EditableMoney, EditableText } from '../../components/Editable';
import { count, money } from '../../lib/format';
import { channel, type DeliveryKind, pricedLine, productLabel, tacticDeliveryMetrics, tacticFor } from '../../lib/plan';
import { useBindings } from '../../state/BindingsContext';
import { useContentLibrary } from '../../state/ContentLibraryContext';
import { knobClasses } from './knobs';
import { Frame, StatTile, TagPill } from './primitives';
import { DualPathPlan } from './ProductPlanDualPath';
import { AudiencePlan } from './ProductPlanAudience';
import { AwarenessPlan } from './ProductPlanAwareness';

/**
 * SEARCH THEMES (audit Slide 10 §4; `docs/momentum-v3-slides.md` product_plan):
 * cpc channels only, capped at 8. `paid_search` reads the same
 * `mediaPlan.keywords` list momentum_v1/v2 use; any other cpc channel with no
 * keyword source falls back to the rep's own `content.bullets`, kept
 * editable exactly like the v1/v2 chip list did.
 */
function searchThemeChips(
  proposal: Proposal,
  productId: string | undefined,
  kind: DeliveryKind | undefined,
  content: DocumentSectionContent,
): { chips: string[]; editable: boolean } {
  if (kind !== 'cpc' || !productId) return { chips: [], editable: false };
  if (productId === 'paid_search' && proposal.mediaPlan.keywords.length > 0) {
    return { chips: proposal.mediaPlan.keywords.slice(0, 8).map((kw) => kw.keyword), editable: false };
  }
  return { chips: (content.bullets ?? []).slice(0, 8), editable: true };
}

/**
 * PRIORITY AUDIENCES (`docs/momentum-v3-slides.md` product_plan; audit
 * Slides 13-14 §3 / Slide 17): reuse the exact `audience.segments` names the
 * Audience slide already established, keyed by `tactic.audienceSegmentIds` -
 * never a second, invented audience taxonomy. Omitted (returns []) when the
 * tactic has no explicit ids.
 */
function priorityAudienceNames(proposal: Proposal, productId: string | undefined): string[] {
  if (!productId) return [];
  const ids = tacticFor(proposal, productId)?.audienceSegmentIds;
  if (!ids?.length) return [];
  const byId = new Map(proposal.audience.segments.map((segment) => [segment.id, segment]));
  const names = ids
    .map((id) => byId.get(id))
    .filter((segment): segment is AudienceSegment => !!segment)
    .map((segment) => segment.name || segment.segment || '')
    .filter(Boolean);
  return names.slice(0, 3);
}

/** PLACEMENT / MESSAGE (`docs/momentum-v3-slides.md` product_plan): the tactic's own CLIENT-SAFE usage facts, omitted when the strategy layer hasn't authored any. */
function placementMessageItems(proposal: Proposal, productId: string | undefined): TacticUsageItem[] {
  if (!productId) return [];
  return tacticFor(proposal, productId)?.usage ?? [];
}

/**
 * product_plan (`docs/momentum-v3-slides.md`; audit Slides 10/13-14/17 -
 * REBUILD). Two-zone layout: a left plan block (eyebrow, human headline,
 * one MONTHLY BUDGET line, HOW WE'LL USE IT body, optional SEARCH THEMES /
 * PRIORITY AUDIENCES / PLACEMENT-MESSAGE sub-blocks, each omitted with no
 * data) and a right metric rail keyed off delivery kind
 * (`tacticDeliveryMetrics`, the same bound reader `TacticMeasurementPage`
 * uses, plus V15's `estConversionsMonthly` for the cpc KPI line). No budget
 * tile on the right - the budget line above is the only one on the slide.
 * Zero truncation: no `clampLines` anywhere on this page.
 */
export function ProductPlanPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const resolve = useBindings();
  const productId = section.productId;
  const { productName } = useContentLibrary();
  const label = productId ? (productName(productId) ?? productLabel(proposal, productId)) : section.title;
  // `density`/`headlineSize` (docs/composer-cards.md §13f "ProductPlanPage"):
  // one page-root class list, shared by single_plan and the three
  // variants below (dual_path/audience_plan/awareness_plan) - they all
  // mount through this same `mv3-plan-root` shell, so one set of
  // `momentum-v3*.css` rules keyed off it reaches every layout.
  const rootClassName = ['mv3-plan-root', ...knobClasses(section)].join(' ');

  if (section.content.variant === 'dual_path') {
    return (
      <PageShell
        section={section}
        readOnly={readOnly}
        onTitleSave={(title) => actions.saveTitle(section, title)}
        className={rootClassName}
        hideTitle
      >
        <Frame surface="light" proposal={proposal} section={section}>
          <DualPathPlan proposal={proposal} section={section} actions={actions} readOnly={readOnly} label={label} />
        </Frame>
      </PageShell>
    );
  }

  if (section.content.variant === 'audience_plan') {
    return (
      <PageShell
        section={section}
        readOnly={readOnly}
        onTitleSave={(title) => actions.saveTitle(section, title)}
        className={rootClassName}
        hideTitle
      >
        <Frame surface="light" proposal={proposal} section={section}>
          <AudiencePlan proposal={proposal} section={section} actions={actions} readOnly={readOnly} label={label} />
        </Frame>
      </PageShell>
    );
  }

  if (section.content.variant === 'awareness_plan') {
    return (
      <PageShell
        section={section}
        readOnly={readOnly}
        onTitleSave={(title) => actions.saveTitle(section, title)}
        className={rootClassName}
        hideTitle
      >
        <Frame surface="light" proposal={proposal} section={section}>
          <AwarenessPlan proposal={proposal} section={section} actions={actions} readOnly={readOnly} label={label} />
        </Frame>
      </PageShell>
    );
  }

  const line = productId ? channel(proposal, productId) : null;
  const metrics = productId ? tacticDeliveryMetrics(proposal, productId) : null;
  const priced = productId ? pricedLine(proposal, productId) : null;
  const conversions = priced?.estConversionsMonthly ?? null;

  const headline = section.content.headline || label;
  const body = section.content.body || line?.rationale || '';
  const { chips: themeChips, editable: themeEditable } = searchThemeChips(
    proposal,
    productId,
    metrics?.kind,
    section.content,
  );
  const showThemes = metrics?.kind === 'cpc' && (themeChips.length > 0 || (themeEditable && !readOnly));
  const audienceNames = priorityAudienceNames(proposal, productId);
  const placementItems = placementMessageItems(proposal, productId);
  const fields = section.content.fields ?? {};
  const measurementNote = fields.measurementNote ?? '';

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className={rootClassName}
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-tacticplan-frame">
          <div className="mv3-tacticplan-main">
            <div className="mv3-eyebrow">{label}</div>
            <div className="mv3-tacticplan-headline">
              <EditableText
                value={headline}
                field="headline"
                placeholder={label}
                readOnly={readOnly}
                variant="headline"
                onSave={(next) => actions.saveContent(section, { headline: next })}
              />
            </div>
            {productId && line ? (
              <div className="mv3-tacticplan-budget">
                <span className="mv3-tacticplan-budget-label">Monthly budget</span>
                <span className="mv3-tacticplan-budget-value">
                  <EditableMoney
                    value={line.monthlyBudget}
                    display={money(line.monthlyBudget)}
                    readOnly={readOnly}
                    label={`${label} monthly budget`}
                    onSave={(next) => actions.setChannelBudget(productId, next, label)}
                  />
                </span>
              </div>
            ) : null}
            <div className="mv3-tacticplan-block">
              <div className="mv3-eyebrow">How we'll use it</div>
              <div className="mv3-tacticplan-body">
                <EditableText
                  value={body}
                  field="body"
                  placeholder="Describe how this channel will be used for this advertiser"
                  readOnly={readOnly}
                  onSave={(next) => actions.saveContent(section, { body: next })}
                />
              </div>
            </div>
            {showThemes ? (
              <div className="mv3-tacticplan-block">
                <div className="mv3-eyebrow">Search themes</div>
                {themeEditable ? (
                  <EditableBullets
                    value={section.content.bullets}
                    readOnly={readOnly}
                    placeholder="Add up to 8 short search themes, one per line"
                    onSave={(next) => actions.saveContent(section, { bullets: next.slice(0, 8) })}
                  />
                ) : (
                  <div className="mv3-tacticplan-chips">
                    {themeChips.map((chip, index) => (
                      <TagPill tone="ghost" key={`${chip}-${index}`}>
                        {chip}
                      </TagPill>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
            {audienceNames.length > 0 ? (
              <div className="mv3-tacticplan-block">
                <div className="mv3-eyebrow">Priority audiences</div>
                <div className="mv3-tacticplan-chips">
                  {audienceNames.map((name) => (
                    <TagPill tone="ghost" key={name}>
                      {resolve(name)}
                    </TagPill>
                  ))}
                </div>
              </div>
            ) : null}
            {placementItems.length > 0 ? (
              <div className="mv3-tacticplan-block">
                <div className="mv3-eyebrow">Placement / message</div>
                <ul className="mv3-tacticplan-usage">
                  {placementItems.map((item, index) => (
                    <li key={`${item.label}-${index}`}>
                      <span className="mv3-tacticplan-usage-label">{resolve(item.label)}</span>
                      <span className="mv3-tacticplan-usage-value">{resolve(item.value)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
          <div className="mv3-tacticplan-rail">
            {productId && line && metrics ? (
              <>
                {metrics.kind === 'cpc' ? (
                  <>
                    <StatTile className="mv3-tacticplan-tile" label="Est. clicks" value={count(metrics.clicks)} />
                    {conversions !== null ? (
                      <StatTile
                        className="mv3-tacticplan-tile"
                        label="Est. conversions / mo"
                        value={count(conversions)}
                      />
                    ) : null}
                  </>
                ) : null}
                {metrics.kind === 'cpm' ? (
                  <StatTile className="mv3-tacticplan-tile" label="Est. impressions" value={count(metrics.impressions)} />
                ) : null}
                {metrics.kind === 'cpv' ? (
                  <StatTile className="mv3-tacticplan-tile" label="Est. views" value={count(metrics.views)} />
                ) : null}
                {metrics.kind === 'flat' ? (
                  <>
                    <StatTile
                      className="mv3-tacticplan-tile"
                      label={metrics.unitLabel ? `Number of ${metrics.unitLabel}` : 'Units'}
                      value={count(metrics.units)}
                    />
                    <StatTile className="mv3-tacticplan-tile" label="Drop date" value={metrics.dateLabel} />
                  </>
                ) : null}
                {metrics.kind === 'managed' ? (
                  <StatTile className="mv3-tacticplan-tile" label="Fee" value={money(metrics.feeMonthly)} />
                ) : null}
              </>
            ) : (
              <p className="note">This channel is not in the current plan.</p>
            )}
          </div>
          <div className="mv3-tacticplan-note">
            <EditableText
              value={measurementNote}
              field="fields.measurementNote"
              placeholder="Add a one-sentence measurement note (optional)"
              readOnly={readOnly}
              onSave={(next) => actions.saveContent(section, { fields: { measurementNote: next } })}
            />
          </div>
        </div>
      </Frame>
    </PageShell>
  );
}
